import { useEffect, createContext, useState, useMemo, Fragment, lazy, Suspense } from "react";
import { Route, Routes } from "react-router-dom";
import CssBaseline from "@mui/material/CssBaseline";
import { useShallow } from "zustand/react/shallow";

import { getJWT, classNames } from "~/utils/utils";
import { auth } from "~/providers/api";
import { ROUTES, THEME_LS_ITEM, THEMES } from "~/const";
import { userStore } from "~/stores/user";
import { themeStore } from "~/stores/theme";
import { langStore } from "~/stores/lang";
import { ThemeProvider, createTheme } from "@mui/material/styles";

import Header from "~/components/Header";
import Footer from "~/components/Footer";

// nejcastejsi stranky (hlavni stranka, prihlaseni, 404) jsou v hlavnim balicku, ostatni se stahuji az pri otevreni
import SchedulePage from "~/pagesLogged/SchedulePage";
import LoginPage from "~/pagesPublic/LoginPage";
import Page404 from "~/pagesPublic/Page404";
import FillProfileInfo from "../FillProfileInfo";
import Loader from "../Loader";

import "./style.less";

const ProfilePage = lazy(() => import("~/pagesLogged/ProfilePage"));
const WeightPage = lazy(() => import("~/pagesLogged/StatsWeightPage"));
const PreferencesPage = lazy(() => import("~/pagesLogged/PreferencesPage"));
const FavoriteRecipesPage = lazy(() => import("~/pagesLogged/FavoriteRecipesPage"));
const StatsChestPage = lazy(() => import("~/pagesLogged/StatsChestPage"));
const StatsSidePage = lazy(() => import("~/pagesLogged/StatsSidePage"));
const StatsEnergyPage = lazy(() => import("~/pagesLogged/StatsEnergyPage"));
const StatsWaistPage = lazy(() => import("~/pagesLogged/StatsWaistPage"));
const StatsInputPage = lazy(() => import("~/pagesLogged/StatsInputPage"));

const UnitsListPage = lazy(() => import("~/pagesAdmin/UnitsListPage"));
const IngredientsPage = lazy(() => import("~/pagesAdmin/IngredientsPage"));
const MealsListPage = lazy(() => import("~/pagesAdmin/MealsListPage"));
const RecipesPage = lazy(() => import("~/pagesAdmin/RecipesPage"));
const TagsPage = lazy(() => import("~/pagesAdmin/TagsPage"));
const UsersPage = lazy(() => import("~/pagesAdmin/UsersPage"));
const MailTemplatesPage = lazy(() => import("~/pagesAdmin/MailTemplatesPage"));
const MenusPage = lazy(() => import("~/pagesAdmin/MenusPage"));

const RegisterPage = lazy(() => import("~/pagesPublic/RegisterPage"));
const VerifyPage = lazy(() => import("~/pagesPublic/VerifyPage"));
const ConditionsPage = lazy(() => import("~/pagesPublic/ConditionsPage"));
const VerifyEmailPage = lazy(() => import("~/pagesPublic/VerifyEmailPage"));
const UnsubscribePage = lazy(() => import("~/pagesPublic/UnsubscribePage"));
const ForgotPasswordPage = lazy(() => import("~/pagesPublic/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("~/pagesPublic/ResetPasswordPage"));

export const ColorModeContext = createContext({
	setDark: () => {},
	setLight: () => {},
});

export default function Layout() {
	const { themeData, isDark, setDarkTheme, setLightTheme } = themeStore(useShallow(state => ({
		isDark: state.theme.isDark,
		themeData: state.theme.colors,
		setDarkTheme: state.setDarkTheme,
		setLightTheme: state.setLightTheme,
	})));
	// pri zmene jazyka se cela aplikace vykresli znovu (key), aby se prelozily i texty spocitane ve stavu komponent
	const lang = langStore(state => state.lang);
	const layoutClassess = classNames([
		"layout",
		isDark ? "dark-theme" : "",
	]);
	const [mode, setMode] = useState<"light" | "dark">(isDark ? "dark" : "light");
	const colorMode = useMemo(() => ({
		setDark: () => {
			setMode("dark");
		},
		setLight: () => {
			setMode("light");
		},
	}), []);


	const theme = useMemo(() => createTheme({
		palette: {
			mode,
			// CssBaseline barvi body touto barvou - stejna jako pozadi aplikace, jinak je v tmavem rezimu barevny skok
			background: {
				default: THEMES[mode].background,
			},
		},
		components: {
			MuiButton: {
				styleOverrides: {
					root: {
						borderRadius: 0,
					},
				},
			},
		},
	}), [mode]);

	async function loadData() {
		//  zkusime auth pro uzivatele
		const jwt = getJWT();

		try {
			if (jwt) {
				const authData = await auth(jwt);

				if (authData.noErrors) {
					const login = userStore.getState().login;

					login({
						...authData.data,
						jwt,
					});
				}
			}
		} finally {
			// i pri chybe musime pustit dal, jinak by stranka cekala na overeni donekonecna
			userStore.getState().setAuthChecked();
		}
	}

	window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", evt => {
		const mmIsDark = evt.matches;

		if (mmIsDark) {
			localStorage.setItem(THEME_LS_ITEM, "1");
			setDarkTheme();
			colorMode.setDark();
		} else {
			localStorage.removeItem(THEME_LS_ITEM);
			setLightTheme();
			colorMode.setLight();
		}
	});

	function setColors() {
		for (const [prop, color] of Object.entries(themeData)) {
			const varString = `--theme-${prop}`;

			document.documentElement.style.setProperty(varString, color as string);
		}
	}

	useEffect(() => {
		loadData();
	}, []);

	setColors();

	return <div className={layoutClassess}>
		<ColorModeContext.Provider value={colorMode}>
			<ThemeProvider theme={theme}>
				<CssBaseline />
				<Fragment key={lang}>
					<Header />
					<FillProfileInfo />
					<Suspense fallback={<Loader />}>
						<Routes>
							<Route path={ROUTES.login} element={<LoginPage />} />
							<Route path={ROUTES.register} element={<RegisterPage />} />
							<Route path={ROUTES.unitsList} element={<UnitsListPage />} />
							<Route path={ROUTES.ingredients} element={<IngredientsPage />} />
							<Route path={ROUTES.mealsList} element={<MealsListPage />} />
							<Route path={ROUTES.recipes} element={<RecipesPage />} />
							<Route path={ROUTES.tags} element={<TagsPage />} />
							<Route path={ROUTES.schedule} element={<SchedulePage />} />
							<Route path={ROUTES.profile} element={<ProfilePage />} />
							<Route path={ROUTES.conditions} element={<ConditionsPage />} />
							<Route path={ROUTES.weight} element={<WeightPage />} />
							<Route path={ROUTES.root} element={<LoginPage />} />
							<Route path={ROUTES.statsChest} element={<StatsChestPage />} />
							<Route path={ROUTES.statsSide} element={<StatsSidePage />} />
							<Route path={ROUTES.statsEnergy} element={<StatsEnergyPage />} />
							<Route path={ROUTES.statsWaist} element={<StatsWaistPage />} />
							<Route path={ROUTES.preferences} element={<PreferencesPage />} />
							<Route path={ROUTES.favoriteRecipes} element={<FavoriteRecipesPage />} />
							<Route path={ROUTES.verify} element={<VerifyPage />} />
							<Route path={ROUTES.users} element={<UsersPage />} />
							<Route path={ROUTES.mailTemplates} element={<MailTemplatesPage />} />
							<Route path={ROUTES.menus} element={<MenusPage />} />
							<Route path={ROUTES.verifyEmail} element={<VerifyEmailPage />} />
							<Route path={ROUTES.unsubscribe} element={<UnsubscribePage />} />
							<Route path={ROUTES.forgotPassword} element={<ForgotPasswordPage />} />
							<Route path={ROUTES.resetPassword} element={<ResetPasswordPage />} />
							<Route path={ROUTES.statsInput} element={<StatsInputPage />} />
							<Route path="*" element={<Page404 />} />
						</Routes>
					</Suspense>
					<Footer />
				</Fragment>
			</ThemeProvider>
		</ColorModeContext.Provider>
	</div>;
}
